/* ======================= AUDIO ENGINE ======================= */
import { isIOS } from "./mobile";
let AC=null, master=null, revSend=null, muted=false;
/* The last node before the output. Held at module scope so the iOS media-
   element route can be swapped in behind it after the graph is built. */
let comp=null, mediaEl=null, streamDest=null;
let scratchGain=null, scratchBP1=null, scratchBP2=null, noiseBuf=null, tensionTimer=null;
function audioInit(){
  /* Not just a guard: a context built outside a gesture is born suspended, and
     the early return meant every later tap re-entered here and left without
     ever resuming it. On iOS that is the difference between sound and silence
     for the whole session. */
  if(AC){ resumeAudio(); return; }
  AC=new (window.AudioContext||window.webkitAudioContext)();
  comp=AC.createDynamicsCompressor();
  comp.threshold.value=-14; comp.knee.value=18; comp.ratio.value=5;
  comp.attack.value=.004; comp.release.value=.18;
  master=AC.createGain(); master.gain.value=.9;
  master.connect(comp).connect(AC.destination);
  /* procedural hall reverb: 1.8s decaying noise impulse */
  const irLen=AC.sampleRate*1.8, ir=AC.createBuffer(2,irLen,AC.sampleRate);
  for(let ch=0;ch<2;ch++){const d=ir.getChannelData(ch);
    for(let i=0;i<irLen;i++)d[i]=(Math.random()*2-1)*Math.pow(1-i/irLen,2.6);}
  const rev=AC.createConvolver(); rev.buffer=ir;
  revSend=AC.createGain(); revSend.gain.value=.5;
  revSend.connect(rev).connect(master);
  /* pink-ish noise bed */
  const nlen=AC.sampleRate*2; noiseBuf=AC.createBuffer(1,nlen,AC.sampleRate);
  const nd=noiseBuf.getChannelData(0); let lpv=0;
  for(let i=0;i<nlen;i++){const w=Math.random()*2-1; lpv=lpv*.93+w*.07; nd[i]=w*.55+lpv*1.3;}
  /* scratch voice: coin-on-latex, not foil.
     A = low drag rumble (lowpass 420), B = main rasp body (bandpass ~900), no high hiss band */
  const src=AC.createBufferSource(); src.buffer=noiseBuf; src.loop=true;
  scratchBP1=AC.createBiquadFilter(); scratchBP1.type="bandpass"; scratchBP1.frequency.value=900; scratchBP1.Q.value=1.3;
  scratchBP2=AC.createBiquadFilter(); scratchBP2.type="lowpass"; scratchBP2.frequency.value=420;
  const g1=AC.createGain(); g1.gain.value=1;
  const g2=AC.createGain(); g2.gain.value=.85;
  scratchGain=AC.createGain(); scratchGain.gain.value=0;
  src.connect(scratchBP1).connect(g1).connect(scratchGain);
  src.connect(scratchBP2).connect(g2).connect(scratchGain);
  scratchGain.connect(master);
  src.start();
}
function sendRev(node,amt){if(amt>0){const s=AC.createGain();s.gain.value=amt;node.connect(s);s.connect(revSend);}}
function tone(f,dur=.15,type="sine",vol=.3,when=0,revAmt=0,bendTo=null,att=.008){
  if(!AC||muted)return;
  const t=AC.currentTime+when, o=AC.createOscillator(), g=AC.createGain();
  o.type=type; o.frequency.setValueAtTime(Math.max(30,f),t);
  if(bendTo)o.frequency.exponentialRampToValueAtTime(Math.max(30,bendTo),t+dur);
  g.gain.setValueAtTime(0,t); g.gain.linearRampToValueAtTime(vol,t+att);
  g.gain.exponentialRampToValueAtTime(.0008,t+dur);
  o.connect(g); g.connect(master); sendRev(g,revAmt);
  o.start(t); o.stop(t+dur+.1);
}
function beep(f,dur=.12,type="triangle",vol=.35,when=0){tone(f,dur,type,vol,when,.15);}
function noiseBurst(dur=.1,fType="highpass",fFreq=3000,vol=.2,when=0,revAmt=0,rate=1){
  if(!AC||muted)return;
  const t=AC.currentTime+when, src=AC.createBufferSource();
  src.buffer=noiseBuf; src.playbackRate.value=rate;
  const f=AC.createBiquadFilter(); f.type=fType; f.frequency.value=fFreq;
  const g=AC.createGain();
  g.gain.setValueAtTime(vol,t); g.gain.exponentialRampToValueAtTime(.0008,t+dur);
  src.connect(f).connect(g); g.connect(master); sendRev(g,revAmt);
  src.start(t,Math.random()*1.5,dur+.05);
}
/* real bell physics: inharmonic partials, independent decays, hall wash */
function bellStrike(f0=1568,vol=.5,when=0){
  if(!AC||muted)return;
  [[1,1,.9],[2.0,.55,.65],[2.74,.34,.5],[3.76,.2,.38],[5.4,.11,.26]].forEach(([r,a,d])=>{
    const det=1+(Math.random()-.5)*.004;
    tone(f0*r*det,d,"sine",vol*a,when,.55,null,.003);
  });
  noiseBurst(.02,"highpass",4200,vol*.35,when,.2,1.4);
}
/* metallic coin ping: pitch-dropping partial pair + transient tick */
function pingCoin(vol=.16,when=0,base){
  base=base||1800+Math.random()*900;
  tone(base,.1,"sine",vol,when,.3,base*.72,.002);
  tone(base*1.62,.06,"sine",vol*.5,when,.3,null,.002);
  noiseBurst(.014,"highpass",6500,vol*.5,when,0,1.6);
}
/* brass stab: detuned saw pair through a sweeping lowpass */
function brass(f,when=0,dur=.28,vol=.3){
  if(!AC||muted)return;
  const t=AC.currentTime+when, lp=AC.createBiquadFilter(), g=AC.createGain();
  lp.type="lowpass"; lp.Q.value=1;
  lp.frequency.setValueAtTime(700,t); lp.frequency.exponentialRampToValueAtTime(3800,t+dur*.5);
  lp.frequency.exponentialRampToValueAtTime(1100,t+dur);
  g.gain.setValueAtTime(0,t); g.gain.linearRampToValueAtTime(vol,t+.015);
  g.gain.exponentialRampToValueAtTime(.001,t+dur);
  [-6,6].forEach(cents=>{
    const o=AC.createOscillator(); o.type="sawtooth";
    o.frequency.value=f*Math.pow(2,cents/1200);
    o.connect(lp); o.start(t); o.stop(t+dur+.05);
  });
  lp.connect(g); g.connect(master); sendRev(g,.3);
}
function crash(when=0){noiseBurst(1.25,"highpass",3600,.32,when,.6,.8);}
function tickSnd(){pingCoin(.09,0,2300+Math.random()*500);}
function bell(when=0,vol=.5){bellStrike(1568,vol,when);}
function dingDingDing(){bellStrike(1568,.5,0);bellStrike(1568,.5,.27);bellStrike(1568,.5,.54);bellStrike(2093,.55,.95);}
function fanfare(){
  [523,659,784,1047].forEach((f,i)=>{brass(f,i*.095,.24,.26);tone(f*2,.2,"sine",.08,i*.095,.45);});
  [1047,1319,1568].forEach(f=>brass(f,.42,.55,.2));
  bellStrike(2093,.3,.42);
}
function megafanfare(){
  [392,523,659,784,1047,1319,1568].forEach((f,i)=>brass(f,i*.11,.26,.27));
  crash(.77);
  bellStrike(1568,.45,.82); bellStrike(2093,.42,.98); bellStrike(2637,.38,1.14);
  for(let i=0;i<12;i++)pingCoin(.1,1.0+i*.06,1400+i*260);
}
function siren(){
  if(!AC||muted)return;
  const o=AC.createOscillator(), g=AC.createGain(), t=AC.currentTime;
  o.type="sawtooth"; g.gain.value=.11;
  o.frequency.setValueAtTime(480,t);
  for(let i=0;i<3;i++){
    o.frequency.linearRampToValueAtTime(1250,t+.45+i*.9);
    o.frequency.linearRampToValueAtTime(480,t+.9+i*.9);
  }
  g.gain.setValueAtTime(.11,t+2.5); g.gain.exponentialRampToValueAtTime(.001,t+3);
  o.connect(g); g.connect(master); sendRev(g,.35);
  o.start(t); o.stop(t+3.1);
}
function whoosh(){
  if(!AC||muted)return;
  const t=AC.currentTime, src=AC.createBufferSource(); src.buffer=noiseBuf; src.playbackRate.value=.5;
  const f=AC.createBiquadFilter(); f.type="lowpass";
  const g=AC.createGain();
  f.frequency.setValueAtTime(380,t); f.frequency.exponentialRampToValueAtTime(4400,t+.28);
  g.gain.setValueAtTime(0,t); g.gain.linearRampToValueAtTime(.26,t+.1);
  g.gain.exponentialRampToValueAtTime(.001,t+.5);
  src.connect(f).connect(g); g.connect(master); sendRev(g,.25);
  src.start(t,0,.6);
}
function womp(){ /* polite NL-style loss: soft minor fall, no punishment */
  tone(311,.42,"triangle",.2,0,.3,262);
  tone(233,.55,"triangle",.16,.18,.3,196);
}
function riser(){
  if(!AC||muted)return;
  const o=AC.createOscillator(), f=AC.createBiquadFilter(), g=AC.createGain(), t=AC.currentTime;
  o.type="sawtooth"; f.type="lowpass";
  o.frequency.setValueAtTime(150,t); o.frequency.exponentialRampToValueAtTime(1150,t+.42);
  f.frequency.setValueAtTime(450,t); f.frequency.exponentialRampToValueAtTime(5600,t+.42);
  g.gain.setValueAtTime(.02,t); g.gain.linearRampToValueAtTime(.18,t+.38);
  g.gain.exponentialRampToValueAtTime(.001,t+.5);
  o.connect(f).connect(g); g.connect(master); sendRev(g,.3);
  o.start(t); o.stop(t+.52);
  noiseBurst(.44,"bandpass",2600,.1,0,.3,1.2);
  let d=0;[.0,.1,.19,.27,.34,.395,.435].forEach(w=>pingCoin(.05,w,900+(d++)*160));
}
function payClink(p){pingCoin(.13,0,1300+p*1900);}
function coinClink(n=6){for(let i=0;i<n;i++)pingCoin(.11+Math.random()*.05,i*.055,1600+Math.random()*1100);}
function stinger(){
  pingCoin(.16,0,1300); pingCoin(.17,.1,1750);
  bellStrike(2093,.3,.2);
}
function grit(){ /* dark crunch grain — coating giving way under the coin */
  noiseBurst(.012+Math.random()*.022,"bandpass",420+Math.random()*1050,.14+Math.random()*.12,0,0,.45+Math.random()*.5);
}
function scratchSound(speed){
  if(!AC||muted)return;
  /* stick-slip jitter + auto-decay: sound exists only while the pointer is moving.
     Each movement re-arms the gain then schedules its own fade, so a stationary or
     lost pointer always goes silent within ~150ms — nothing can stick on. */
  const v=Math.min(.48,.06+speed*.014)*(.8+Math.random()*.4);
  const t=AC.currentTime;
  scratchGain.gain.cancelScheduledValues(t);
  scratchGain.gain.setTargetAtTime(v,t,.018);
  scratchGain.gain.setTargetAtTime(0,t+.12,.05);
  scratchBP1.frequency.setTargetAtTime(720+Math.min(800,speed*42),t,.03);
}
function scratchStop(){
  if(!AC)return;
  scratchGain.gain.cancelScheduledValues(AC.currentTime);
  scratchGain.gain.setTargetAtTime(0,AC.currentTime,.045);
}
/* tension = a literal heartbeat, synced to the 0.9s visual pulse */
let hbRate=900;
function tensionStart(rate){
  if(!AC||muted)return;
  if(tensionTimer&&rate===hbRate)return;      /* already beating at this tempo */
  if(tensionTimer)clearInterval(tensionTimer); /* tempo change → rebuild */
  hbRate=rate||900;
  const thump=()=>{
    if(muted)return;
    /* LUB — sub for big speakers, mids for phones, noise transient for the punch.
       58Hz alone is inaudible on laptop/phone drivers; the 120/180Hz partials
       and the lowpassed click are what actually make this land. */
    tone(58,.17,"sine",.55,0,0,42,.004);
    tone(120,.15,"sine",.34,0,0,86,.004);
    tone(180,.10,"triangle",.19,0,0,128,.003);
    noiseBurst(.05,"lowpass",340,.22,0,0,.5);
    /* DUB */
    tone(52,.15,"sine",.40,.23,0,40,.004);
    tone(104,.12,"sine",.24,.23,0,78,.004);
    tone(158,.08,"triangle",.13,.23,0,112,.003);
    noiseBurst(.04,"lowpass",300,.15,.23,0,.5);
  };
  thump(); tensionTimer=setInterval(thump,hbRate);
}
function tensionStop(){
  if(tensionTimer){clearInterval(tensionTimer);tensionTimer=null;}
}

/* Scrape bed: driven by the scratch gesture in core-game, but it manipulates
   engine-internal nodes, so it lives with the engine. */
function scrapeOn(){ if(!AC||muted)return;
  scratchGain.gain.cancelScheduledValues(AC.currentTime);
  scratchGain.gain.setTargetAtTime(.5,AC.currentTime,.05);
  scratchBP1.frequency.setTargetAtTime(620,AC.currentTime,.05);
}
function scrapeOff(){ if(!AC)return;
  scratchGain.gain.cancelScheduledValues(AC.currentTime);
  scratchGain.gain.setTargetAtTime(0,AC.currentTime,.06);
  scratchBP1.frequency.setTargetAtTime(900,AC.currentTime,.2);
}


/**
 * Route the graph out through an <audio> element, for iOS only.
 *
 * iOS mutes the Web Audio output when the hardware ring/silent switch is set
 * to silent — a game whose whole soundtrack is procedural is then simply mute,
 * with nothing on screen to explain why. An HTMLMediaElement plays on the
 * media channel instead, which ignores that switch, so piping the graph into
 * one through a MediaStream buys the entire game that channel.
 *
 * Failure here must never be worse than not trying. The direct connection to
 * AC.destination is kept until the element is confirmed playing, and a
 * watchdog puts it back if the element is stopped or the track dies — both of
 * which iOS does under memory pressure or an interrupting call.
 */
function routeThroughMediaElement(){
  if(!AC||!comp||mediaEl)return;
  if(!isIOS())return;                    /* nothing else needs this, and the
                                            extra hop costs latency */
  if(typeof AC.createMediaStreamDestination!=="function")return;
  try{
    streamDest=AC.createMediaStreamDestination();
    comp.connect(streamDest);

    const el=document.createElement("audio");
    /* Without playsinline iOS may take the stream fullscreen. */
    el.setAttribute("playsinline","");
    el.setAttribute("webkit-playsinline","");
    el.autoplay=true;
    el.srcObject=streamDest.stream;

    /* play() must be CALLED inside the gesture; awaiting its promise after is
       fine. A rejection means we simply keep the direct path. */
    const started=el.play();
    if(started&&typeof started.then==="function"){
      started.then(()=>{
        /* Only now is it safe to stop using the default output, so a rejected
           or silent start cannot leave the game with no path at all. */
        try{ comp.disconnect(AC.destination); }catch(e){}
        mediaEl=el;
        watchMediaElement(el);
      }).catch(()=>{ teardownMediaRoute(); });
    }else{
      teardownMediaRoute();
    }
  }catch(e){ teardownMediaRoute(); }
}

/** Put the direct output back and forget the element. */
function teardownMediaRoute(){
  try{ if(streamDest&&comp)comp.disconnect(streamDest); }catch(e){}
  try{ if(mediaEl){ mediaEl.pause(); mediaEl.srcObject=null; } }catch(e){}
  streamDest=null; mediaEl=null;
  /* connect() on an already-connected pair is a no-op, so this is safe to
     call whether or not the direct path was ever removed. */
  try{ if(comp&&AC)comp.connect(AC.destination); }catch(e){}
}

/**
 * iOS stops media elements on an interrupting call or under memory pressure.
 * If that happens the game would go silent with no way back, so watch for it
 * and fall back to the ordinary output.
 */
function watchMediaElement(el){
  const check=()=>{
    if(el!==mediaEl)return;
    const track=streamDest&&streamDest.stream.getAudioTracks()[0];
    if(el.paused||!track||track.readyState==="ended"){
      /* Try once to resume in place before giving the channel up. */
      const again=el.play();
      if(again&&typeof again.catch==="function")again.catch(()=>teardownMediaRoute());
      else if(el.paused)teardownMediaRoute();
    }
  };
  el.addEventListener("pause",check);
  document.addEventListener("visibilitychange",()=>{ if(!document.hidden)check(); });
}

/**
 * Unlock audio on the first gesture anywhere on the page.
 *
 * Individual handlers called audioInit() one by one, so whether sound ever
 * started depended on WHICH control the player happened to touch first — the
 * scratch canvas resumed the context, the header buttons did not. A capture
 * listener on the document removes that lottery.
 *
 * iOS additionally requires the resume to happen INSIDE the gesture, not in a
 * promise chain after it, which is why this is synchronous.
 */
export function installAudioUnlock(){
  const unlock=()=>{
    audioInit();
    resumeAudio();
    /* Called synchronously inside the gesture: iOS only honours play() there. */
    routeThroughMediaElement();
    /* Only remove once the context is actually running: a resume during a
       gesture the browser does not consider trusted can silently no-op, and
       giving up after one try would strand the session in silence. */
    if(AC&&AC.state==="running"){
      for(const ev of ["pointerdown","touchstart","keydown"])
        document.removeEventListener(ev,unlock,true);
    }
  };
  for(const ev of ["pointerdown","touchstart","keydown"])
    document.addEventListener(ev,unlock,true);

  /* iOS suspends the context when the tab is backgrounded — including the trip
     out to a wallet app and back, which this game does routinely. */
  document.addEventListener("visibilitychange",()=>{
    if(!document.hidden)resumeAudio();
  });
}

/* Browsers start the context suspended until a gesture; callers used to poke
   AC.state directly, which no longer crosses the module boundary. */
export function resumeAudio(){ if(AC&&AC.state==="suspended")AC.resume(); }

/* Mute is owned by the audio engine; the button lives in core-game, which
   passes the current tension state so a silenced heartbeat can be revived. */
export function toggleMute(isTensionActive){
  muted=!muted;
  if(muted){scratchStop();tensionStop();}
  else if(isTensionActive)tensionStart(hbRate); /* was silenced, not cancelled — bring it back */
  return muted;
}

export {
  audioInit, tone, beep, noiseBurst, bellStrike, pingCoin, brass, crash,
  tickSnd, bell, dingDingDing, fanfare, megafanfare, siren, whoosh, womp,
  riser, payClink, coinClink, stinger, grit, scratchSound, scratchStop,
  hbRate, tensionStart, tensionStop, scrapeOn, scrapeOff
};
export const isMuted=()=>muted;
export const audioCtx=()=>AC;
